import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import EventArrowIconV4 from './EventArrowIconV4';
import EventLineV4 from './EventLineV4';
import EventRowItemV4 from './EventRowItemV4';
import ModuleSectionV4 from './ModuleSectionV4';
import {CommentThemeContext} from '../../comment/common';
import CommentContent from '../../comment/component/CommentContent';
import EventCommentModal from '../../comment/container/EventCommentModal';
import {ICommentTheme} from '../../comment/type';
import SingleText from '../../common/component/SingleText';
import Text from '../../common/component/Text';
import {useFetchInfo} from '../../common/util/fetch';
import {formatNumber} from '../../common/util/number';
import LikeInfoV4, {
  LikeInfoHandlerV4,
} from '../../commonSub/component/LikeInfoV4';
import TextButton from '../../ui/button/TextButton';
import {EventCommonContext} from '../common';
import useEventPageEvent from '../hook/useEventPageEvent';
import {ActionType} from '../state/action';
import {ICommentModuleV4} from '../type';

interface IProps {
  data: ICommentModuleV4;
}

export default function CommentModuleV4({data}: IProps) {
  const {t} = useTranslation();
  const {likeInfo, commentList} = data;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {eventId, questCampaignId, bgColor} = useContext(EventCommonContext);
  const {totalCount: commentCount} = useFetchInfo(
    ActionType.FetchEventComment,
    eventId
  );

  const isEmpty = commentCount === 0;

  const commentSection = {
    title: isEmpty
      ? t('viewer_end_comment_count_zero')
      : t('viewer_end_comment_count_v4', {
          value: formatNumber(commentCount),
        }),
  };

  const handleClickViewCommentList = () => {
    setIsModalOpen(true);
  };

  const handleClickLike: LikeInfoHandlerV4 = () => {
    // TODO: V4 좋아요 기능
  };

  const sendEventPageEvent = useEventPageEvent();
  const handleCloseModal = () => {
    sendEventPageEvent({actionName: 'comment_more'});
    setIsModalOpen(false);
  };

  if (!eventId || !questCampaignId) {
    return null;
  }

  return (
    <ModuleSectionV4
      data-module-type={data.moduleType}
      section={commentSection}
      innerClassName="flex flex-col gap-y-20"
    >
      {likeInfo && (
        <LikeInfoV4
          theme="dark"
          {...likeInfo}
          // TODO: V4 API 연동하면 변경하자
          fetchStatus="fail"
          onClickThumb={handleClickLike}
        />
      )}
      <div
        className="rounded-12 px-18 bg-white/15 cursor-pointer"
        onClick={handleClickViewCommentList}
      >
        <EventRowItemV4
          className="min-h-[60px] gap-x-4"
          leftElement={
            <SingleText className="s16-bold-white leading-20">
              {t('viewer_end_comment_button')}
            </SingleText>
          }
          rightElement={<EventArrowIconV4 isWhiteIcon />}
        />
        <EventLineV4 className="opacity-[0.08]" />
        {isEmpty && (
          <Text className="s14-regular-white text-center leading-21 my-50">
            {t('comment_empty_first')}
          </Text>
        )}
        {!isEmpty && (
          <CommentThemeContext.Provider value={EventCommentTheme}>
            <ul className="pt-18 pb-24">
              {commentList.map(comment => (
                <li key={comment.id} className="mt-30 first:mt-0">
                  <CommentContent comment={comment} contentMaxLine={3} />
                </li>
              ))}
            </ul>
          </CommentThemeContext.Provider>
        )}
      </div>
      <div className="flex-center">
        <TextButton
          className="btn-white h-48 px-30"
          textClassName="s15-bold-black leading-19 tracking-[-0.4px] "
          style={{
            boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.08)',
          }}
          onClick={handleClickViewCommentList}
        >
          {t('crm_comment_button')}
        </TextButton>
      </div>
      <CommentThemeContext.Provider value={EventCommentTheme}>
        {/* TODO : V4 이벤트 페이지 스포일러 기능 제외 (스포일러 체크기능 삭제) */}
        <EventCommentModal
          eventId={eventId}
          questCampaignId={questCampaignId}
          isOpen={isModalOpen}
          bgColor={bgColor}
          onRequestClose={handleCloseModal}
        />
      </CommentThemeContext.Provider>
    </ModuleSectionV4>
  );
}

const EventCommentTheme: ICommentTheme = {
  commentBg: 'bg-transparent',
  replyCommentBg: 'bg-white/4',
  replyIconColor: 'grey',
  isOnlyDarkModeBadge: true,
  createDtTextStyle: 's12-regular-white opacity-30',
  spoilerTextStyle: 's14-regular-white opacity-2',
  spoilerInfoTextStyle: 's14-regular-white',
  moreIconColor: 'white',
  buttonTheme: {
    textStyle: 's12-regular-white opacity-50',
    bgStyle: 'bg-white/4',
    bgStyleForReply: 'bg-white/4',
    smallDotVariant: 'white',
    effectStyle: {
      color: {
        textStyle: 's12-regular-white',
        bgStyle: 'bg-white/4',
        bgStyleForReply: 'bg-white/4',
      },
      comment: {
        textStyle: 's12-regular-white',
        bgStyle: 'bg-white/4',
        bgStyleForReply: 'bg-white/4',
      },
    },
  },
  deleteBtnTextStyle: 's12-regular-white',
  replyMoreTextStyle: 's14-semibold-white',
  replyInputVariant: 'white',
  temporaryTextStyle: 's14-regular-white opacity-30',
  temporaryUserNameTextStyle: 's13-bold-white opacity-30',
  emptyTextStyle: 's16-regular-white',
  userNameTextStyle: 's13-bold-white',
  withdrawUserNameTextStyle: 's13-bold-white opacity-30',
  contentTextStyle: 's14-regular-white',
  commentDividerColor: 'bg-white/8',
  replyDividerColor: 'bg-white/8',
  inputWrapperStyle: 'bg-white !border-none',
  inputWrapperStyleForReply: 'bg-white/15 border-white/4',
  bestBadgeColor: 'white',
};
